"use client";

import { useState, useEffect, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Vehicle, OneTimePackage } from "@/types";
import { VehicleMultiSelect } from "@/components/appointment/VehicleMultiSelect";
import { repeatedAppointmentService } from "@/services/repeatedAppointmentService";
import { userSubscriptionService } from "@/services/userSubscriptionService";
import {
  RepeatedAppointmentWithDetails,
  WEEKDAYS,
} from "@/types/repeatedAppointment.types";
import { UserSubscriptionGetAllOutput } from "@/types/userSubscription.types";
import { generateTimeSlots, formatCurrency } from "@/lib/utils";
import { toast } from "sonner";
import {
  Loader2,
  Plus,
  Trash2,
  Edit2,
  Package,
  CreditCard,
} from "lucide-react";

const timeSlots = generateTimeSlots();

// Helper to normalize time format for Select matching
// Backend may store "9:15AM" but Select uses "9:15 AM" as value
const normalizeTimeValue = (time: string | null): string => {
  if (!time) return "";
  // Remove all spaces and convert to uppercase for comparison
  const normalized = time.replace(/\s+/g, "").trim().toUpperCase();
  // Find matching slot by comparing normalized formats
  const slot = timeSlots.find(
    (s) => s.label.replace(/\s+/g, "").trim().toUpperCase() === normalized,
  );
  return slot ? slot.label : "";
};

// Type for setup type selection
type SetupType = "package" | "subscription";

interface RepeatedAppointmentSetupProps {
  customerId: string;
  customerVehicles: Vehicle[];
  packages: OneTimePackage[];
}

export function RepeatedAppointmentSetup({
  customerId,
  customerVehicles,
  packages,
}: RepeatedAppointmentSetupProps) {
  // Existing setups from backend
  const [existingSetups, setExistingSetups] = useState<
    RepeatedAppointmentWithDetails[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // COMMENTED OUT: Subscription functionality disabled
  // Customer subscriptions for selection
  // const [customerSubscriptions, setCustomerSubscriptions] = useState<
  //   UserSubscriptionGetAllOutput[]
  // >([]);
  // const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);

  // Form state for new/editing setup
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  // COMMENTED OUT: Force package type only (subscription disabled)
  const [setupType] = useState<SetupType>("package");
  const [selectedPackageId, setSelectedPackageId] = useState("");
  // const [selectedSubscriptionId, setSelectedSubscriptionId] = useState("");
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<string[]>([]);
  const [preferredDay, setPreferredDay] = useState("");
  const [preferredTime, setPreferredTime] = useState("");
  const [vehicleSearchTerm, setVehicleSearchTerm] = useState("");

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Fetch existing setups
  const fetchSetups = useCallback(async () => {
    if (!customerId) return;

    setLoading(true);
    try {
      const setups =
        await repeatedAppointmentService.getByCustomerId(customerId);
      setExistingSetups(setups);
    } catch (error) {
      console.error("Error fetching repeated appointments:", error);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  // COMMENTED OUT: Subscription fetch functionality disabled
  // Fetch customer's active subscriptions
  // const fetchSubscriptions = useCallback(async () => {
  //   if (!customerId) return;
  //
  //   setLoadingSubscriptions(true);
  //   try {
  //     const allSubs = await userSubscriptionService.getAllUserSubscriptions();
  //     // Filter for this customer's active subscriptions
  //     const customerSubs = allSubs.filter(
  //       (sub) => sub.customerId === customerId && sub.isActive,
  //     );
  //     setCustomerSubscriptions(customerSubs);
  //   } catch (error) {
  //     console.error("Error fetching customer subscriptions:", error);
  //   } finally {
  //     setLoadingSubscriptions(false);
  //   }
  // }, [customerId]);

  useEffect(() => {
    fetchSetups();
    // fetchSubscriptions(); // COMMENTED OUT: Subscription disabled
  }, [fetchSetups]);

  const resetForm = () => {
    // COMMENTED OUT: setSetupType("package"); - now constant
    setSelectedPackageId("");
    // setSelectedSubscriptionId(""); // COMMENTED OUT: Subscription disabled
    setSelectedVehicleIds([]);
    setPreferredDay("");
    setPreferredTime("");
    setVehicleSearchTerm("");
    setIsEditing(false);
    setEditingId(null);
  };

  const handleStartNew = () => {
    resetForm();
    setIsEditing(true);
  };

  const handleEdit = (setup: RepeatedAppointmentWithDetails) => {
    setEditingId(setup.id);
    // COMMENTED OUT: Subscription handling disabled
    // Determine setup type based on which ID is populated
    // if (setup.userSubscriptionId) {
    //   setSetupType("subscription");
    //   setSelectedSubscriptionId(setup.userSubscriptionId);
    //   setSelectedPackageId("");
    // } else {
    //   setSetupType("package");
    //   setSelectedPackageId(setup.packageId || "");
    //   setSelectedSubscriptionId("");
    // }
    // Force package type only
    setSelectedPackageId(setup.packageId || "");
    setSelectedVehicleIds(setup.vehicleIds);
    setPreferredDay(setup.preferredDay || "");
    setPreferredTime(normalizeTimeValue(setup.preferredTime)); // Normalize for Select matching
    setIsEditing(true);
  };

  const handleCancel = () => {
    resetForm();
  };

  const handleSave = async () => {
    // Validate based on setup type
    if (setupType === "package" && !selectedPackageId) {
      toast.error("Please select a package");
      return;
    }
    // COMMENTED OUT: Subscription validation disabled
    // if (setupType === "subscription" && !selectedSubscriptionId) {
    //   toast.error("Please select a subscription");
    //   return;
    // }
    if (selectedVehicleIds.length === 0) {
      toast.error("Please select at least one vehicle");
      return;
    }
    if (!preferredDay) {
      toast.error("Please select a preferred day");
      return;
    }
    if (!preferredTime) {
      toast.error("Please select a preferred time");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        // Update existing
        await repeatedAppointmentService.update(editingId, {
          packageId: setupType === "package" ? selectedPackageId : null,
          // COMMENTED OUT: Subscription disabled - always null
          userSubscriptionId: null,
          // userSubscriptionId:
          //   setupType === "subscription" ? selectedSubscriptionId : null,
          vehicleIds: selectedVehicleIds,
          preferredDay,
          preferredTime,
        });
        toast.success("Recurring setup updated successfully");
      } else {
        // Create new
        await repeatedAppointmentService.create({
          customerId,
          packageId: setupType === "package" ? selectedPackageId : null,
          // COMMENTED OUT: Subscription disabled - always null
          userSubscriptionId: null,
          // userSubscriptionId:
          //   setupType === "subscription" ? selectedSubscriptionId : null,
          vehicleIds: selectedVehicleIds,
          preferredDay,
          preferredTime,
        });
        toast.success("Recurring setup created successfully");
      }

      resetForm();
      await fetchSetups();
    } catch (error: any) {
      const message =
        error?.response?.data?.message || "Failed to save recurring setup";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const openDeleteConfirmation = (id: string) => {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingId) return;

    try {
      await repeatedAppointmentService.delete(deletingId);
      toast.success("Recurring setup deleted");
      await fetchSetups();
    } catch (error) {
      toast.error("Failed to delete recurring setup");
    } finally {
      setDeleteDialogOpen(false);
      setDeletingId(null);
    }
  };

  const selectedPackage = packages.find((p) => p.id === selectedPackageId);
  const activePackages = packages.filter((p) => p.active !== false);
  // COMMENTED OUT: Subscription selection disabled
  // const selectedSubscription = customerSubscriptions.find(
  //   (s) => s.id === selectedSubscriptionId,
  // );

  // Helper to get display name for a setup
  const getSetupDisplayName = (
    setup: RepeatedAppointmentWithDetails,
  ): string => {
    if (setup.userSubscription?.subscription?.name) {
      return setup.userSubscription.subscription.name;
    }
    if (setup.package?.name) {
      return setup.package.name;
    }
    return "Setup";
  };

  const getSetupPrice = (setup: RepeatedAppointmentWithDetails): number => {
    if (setup.userSubscription?.subscription?.price) {
      return setup.userSubscription.subscription.price;
    }
    if (setup.package?.price) {
      return setup.package.price;
    }
    return 0;
  };

  // COMMENTED OUT: Subscription check helper disabled
  // const isSubscriptionSetup = (
  //   setup: RepeatedAppointmentWithDetails,
  // ): boolean => {
  //   return !!setup.userSubscriptionId;
  // };
  // Helper to check if a setup is subscription-based (for display only)
  const isSubscriptionSetup = (
    setup: RepeatedAppointmentWithDetails,
  ): boolean => {
    return !!setup.userSubscriptionId;
  };

  return (
    <div className="space-y-4">
      <Separator className="my-4" />

      {/* Existing Setups */}
      {loading ? (
        <div className="flex items-center gap-2 py-4">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm text-muted-foreground">
            Loading recurring setups...
          </span>
        </div>
      ) : existingSetups.length > 0 && !isEditing ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Existing Recurring Setups</h4>
            <Button variant="outline" size="sm" onClick={handleStartNew}>
              <Plus className="h-4 w-4 mr-1" />
              Add New
            </Button>
          </div>
          {existingSetups.map((setup) => (
            <Card key={setup.id} className="p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    {isSubscriptionSetup(setup) ? (
                      <CreditCard className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Package className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className="font-medium">
                      {getSetupDisplayName(setup)}
                    </span>
                    <Badge
                      variant={
                        isSubscriptionSetup(setup) ? "default" : "secondary"
                      }
                    >
                      {isSubscriptionSetup(setup)
                        ? "Subscription"
                        : formatCurrency(getSetupPrice(setup))}
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {setup.preferredDay &&
                      `Every ${setup.preferredDay.charAt(0).toUpperCase()}${setup.preferredDay.slice(1)}`}
                    {setup.preferredTime && ` at ${setup.preferredTime}`}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {setup.vehicles?.map((v) => (
                      <Badge
                        key={v.id}
                        variant="outline"
                        className="text-xs font-normal"
                      >
                        {v.plateNumber}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(setup)}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openDeleteConfirmation(setup.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : null}

      {/* New/Edit Form */}
      {isEditing || existingSetups.length === 0 ? (
        <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">
              {editingId ? "Edit Recurring Setup" : "New Recurring Setup"}
            </h4>
            {existingSetups.length > 0 && (
              <Button variant="ghost" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
            )}
          </div>

          {/* COMMENTED OUT: Setup Type Toggle - Subscription disabled
          <div className="grid gap-2">
            <Label>Setup Type *</Label>
            <RadioGroup
              value={setupType}
              onValueChange={(value: SetupType) => {
                setSetupType(value);
                // Clear the other selection when switching
                if (value === "package") {
                  setSelectedSubscriptionId("");
                } else {
                  setSelectedPackageId("");
                }
              }}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="package" id="type-package" />
                <Label
                  htmlFor="type-package"
                  className="flex items-center gap-1 cursor-pointer"
                >
                  <Package className="h-4 w-4" />
                  Package
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="subscription" id="type-subscription" />
                <Label
                  htmlFor="type-subscription"
                  className="flex items-center gap-1 cursor-pointer"
                >
                  <CreditCard className="h-4 w-4" />
                  Subscription
                </Label>
              </div>
            </RadioGroup>
          </div>
          */}

          {/* Package Selection - shown when setupType is package */}
          {setupType === "package" && (
            <div className="grid gap-2">
              <Label>Package *</Label>
              <Select
                value={selectedPackageId}
                onValueChange={setSelectedPackageId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select package" />
                </SelectTrigger>
                <SelectContent>
                  {activePackages.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      No packages available
                    </div>
                  ) : (
                    activePackages.map((pkg) => (
                      <SelectItem key={pkg.id} value={pkg.id}>
                        <div className="flex items-center gap-2">
                          <span>{pkg.name}</span>
                          <Badge variant="secondary" className="ml-auto">
                            {formatCurrency(pkg.price)}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {selectedPackage && (
                <p className="text-xs text-muted-foreground">
                  {selectedPackage.description}
                </p>
              )}
            </div>
          )}

          {/* COMMENTED OUT: Subscription Selection - Subscription disabled
          {setupType === "subscription" && (
            <div className="grid gap-2">
              <Label>Subscription *</Label>
              <Select
                value={selectedSubscriptionId}
                onValueChange={setSelectedSubscriptionId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select subscription" />
                </SelectTrigger>
                <SelectContent>
                  {loadingSubscriptions ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                      Loading subscriptions...
                    </div>
                  ) : customerSubscriptions.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      No active subscriptions found for this customer
                    </div>
                  ) : (
                    customerSubscriptions.map((sub) => (
                      <SelectItem key={sub.id} value={sub.id}>
                        <div className="flex items-center gap-2">
                          <span>
                            {sub.subscription?.name || "Subscription"}
                          </span>
                          <Badge variant="default" className="ml-auto">
                            Active
                          </Badge>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {selectedSubscription && (
                <p className="text-xs text-muted-foreground">
                  This setup will be linked to the selected subscription
                </p>
              )}
            </div>
          )}
          */}

          {/* Vehicle Selection */}
          <div className="grid gap-2">
            <Label>Vehicles *</Label>
            <VehicleMultiSelect
              vehicles={customerVehicles}
              selectedVehicleIds={selectedVehicleIds}
              onSelectionChange={setSelectedVehicleIds}
              searchTerm={vehicleSearchTerm}
              onSearchChange={setVehicleSearchTerm}
              error={
                selectedVehicleIds.length === 0
                  ? "Select at least one vehicle"
                  : undefined
              }
            />
          </div>

          {/* Day and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Preferred Day *</Label>
              <Select value={preferredDay} onValueChange={setPreferredDay}>
                <SelectTrigger>
                  <SelectValue placeholder="Select day" />
                </SelectTrigger>
                <SelectContent>
                  {WEEKDAYS.map((day) => (
                    <SelectItem key={day.value} value={day.value}>
                      {day.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Preferred Time *</Label>
              <Select value={preferredTime} onValueChange={setPreferredTime}>
                <SelectTrigger>
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((slot) => (
                    <SelectItem key={slot.value} value={slot.label}>
                      {slot.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Summary - MODIFIED: Subscription references removed */}
          {setupType === "package" &&
            selectedPackageId &&
            selectedVehicleIds.length > 0 && (
              <div className="p-3 bg-primary/5 rounded-lg text-sm">
                <p className="font-medium">Summary:</p>
                <p className="text-muted-foreground">
                  {selectedPackage?.name}{" "}
                  for {selectedVehicleIds.length} vehicle
                  {selectedVehicleIds.length !== 1 ? "s" : ""}
                  {preferredDay &&
                    ` every ${preferredDay.charAt(0).toUpperCase()}${preferredDay.slice(1)}`}
                  {preferredTime && ` at ${preferredTime}`}
                </p>
                <p className="font-medium mt-1">
                  Amount per appointment:{" "}
                  {formatCurrency(
                    (selectedPackage?.price || 0) * selectedVehicleIds.length,
                  )}
                </p>
                {/* COMMENTED OUT: Subscription summary disabled
                {setupType === "subscription" && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Subscription-based setup (no per-appointment charge)
                  </p>
                )}
                */}
              </div>
            )}

          {/* Save Button */}
          <Button
            onClick={handleSave}
            disabled={
              saving ||
              (setupType === "package" && !selectedPackageId) ||
              // COMMENTED OUT: Subscription validation disabled
              // (setupType === "subscription" && !selectedSubscriptionId) ||
              selectedVehicleIds.length === 0 ||
              !preferredDay ||
              !preferredTime
            }
            className="w-full"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : editingId ? (
              "Update Recurring Setup"
            ) : (
              "Create Recurring Setup"
            )}
          </Button>
        </div>
      ) : null}

      {/* Empty state - show form when no setups exist */}
      {!loading && existingSetups.length === 0 && !isEditing && (
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground mb-3">
            No recurring appointment setups found
          </p>
          <Button variant="outline" onClick={handleStartNew}>
            <Plus className="h-4 w-4 mr-1" />
            Create First Setup
          </Button>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Recurring Setup?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this recurring appointment setup.
              Future appointments will no longer be generated from this setup.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
